import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, post } from '../../lib/api.ts';
import { useRefreshMe } from '../../lib/auth.tsx';
import { accountMe, enterWorkspace, workspaces, ROLE_LABEL, type Workspace } from '../../lib/account.ts';
import { AuthShell, ErrorText } from '../../components/AuthShell.tsx';
import { Badge } from '../../components/ui/Button.tsx';

/**
 * CR-067 조직 선택: 본인 확인 뒤 소속 조직과 받은 초대를 보여 준다. 소속이 하나뿐이면 바로 들어간다(?switch=1 이면 항상 목록).
 * 들어갈 때 그 조직의 진입 조건(SSO·MFA·비밀번호 변경)을 탄다.
 */
export function WorkspacesPage() {
  const nav = useNavigate(); const qc = useQueryClient(); const refreshMe = useRefreshMe();
  const [sp] = useSearchParams(); const isSwitch = sp.get('switch') === '1';
  const me = useQuery({ queryKey: ['account-me'], queryFn: accountMe, retry: false });
  const ws = useQuery({ queryKey: ['account-workspaces'], queryFn: workspaces, retry: false });
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const auto = useRef(false);

  useEffect(() => { if (ws.error instanceof ApiError && ws.error.status === 401) nav('/login', { replace: true }); }, [ws.error, nav]);

  async function enter(w: Workspace) {
    setError(null); setBusy(w.tenantId);
    try {
      const to = await enterWorkspace(w.tenantId);
      if (to === null) return; // IdP 또는 커스텀 도메인으로 이동 중
      qc.clear(); await refreshMe();
      nav(to, { replace: true });
    } catch (e) { setError(e); setBusy(null); }
  }
  async function accept(w: Workspace) {
    setError(null); setBusy(w.tenantId);
    try { await post(`/account/workspaces/${w.tenantId}/accept`); await ws.refetch(); await enter({ ...w, status: 'active' }); }
    catch (e) { setError(e); setBusy(null); }
  }
  async function logout() { await post('/account/logout'); qc.clear(); nav('/login', { replace: true }); }

  // 소속이 하나뿐이고 초대가 없으면 바로 들어간다
  useEffect(() => {
    if (auto.current || isSwitch || !ws.data) return;
    const active = ws.data.filter((w) => w.status === 'active' && !w.suspended);
    if (active.length === 1 && ws.data.length === 1) { auto.current = true; void enter(active[0]); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.data, isSwitch]);

  const footer = <div className="flex items-center justify-center gap-3"><span>{me.data?.email}</span><span className="text-slate-300">|</span><button type="button" onClick={logout}>로그아웃</button></div>;
  const list = ws.data ?? [];
  return (
    <AuthShell title={isSwitch ? '조직 전환' : '조직 선택'} subtitle="들어갈 조직을 고르세요." noTenant footer={footer}>
      {ws.isLoading ? <div className="text-sm text-slate-500">불러오는 중…</div>
        : list.length === 0 ? <p className="text-[13px] text-slate-600 leading-relaxed">소속된 조직이 없습니다. 조직의 관리자에게 초대를 요청하세요.</p>
        : (
          <ul className="border border-slate-200 divide-y divide-slate-200">
            {list.map((w) => (
              <li key={w.tenantId} className="flex items-center gap-3 px-3 py-2.5">
                <div className="min-w-0 flex-1">
                  <div className="text-[13.5px] font-semibold text-slate-900 truncate">{w.name}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1 text-[11.5px] text-slate-500">
                    <span>{ROLE_LABEL[w.role] ?? w.role}</span>
                    {w.status === 'invited' && <Badge tone="warning">초대</Badge>}
                    {w.suspended && <Badge tone="danger">정지</Badge>}
                    {w.requires.sso && <Badge tone="info">SSO 필요</Badge>}
                    {w.requires.mfa && <Badge tone="neutral">2단계 인증</Badge>}
                    {w.requires.passwordChange && <Badge tone="warning">비밀번호 변경 필요</Badge>}
                  </div>
                </div>
                <button type="button" disabled={busy !== null || w.suspended} onClick={() => (w.status === 'invited' ? accept(w) : enter(w))}
                  className="flex-none h-8 px-3 bg-accent text-white text-[12.5px] font-semibold hover:bg-accent-strong disabled:opacity-50 disabled:cursor-not-allowed">
                  {busy === w.tenantId ? '이동 중…' : w.status === 'invited' ? '수락하고 들어가기' : '들어가기'}
                </button>
              </li>
            ))}
          </ul>
        )}
      <ErrorText error={error ?? (ws.error instanceof ApiError && ws.error.status === 401 ? null : ws.error)} />
    </AuthShell>
  );
}

/** CR-067 매직링크 도착: 토큰 확인 → 계정 세션 → 조직 선택(또는 ?tenant= 조직으로 바로) */
export function MagicLinkPage() {
  const nav = useNavigate(); const [sp] = useSearchParams(); const qc = useQueryClient(); const refreshMe = useRefreshMe();
  const [error, setError] = useState<unknown>(null);
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return; ran.current = true;
    (async () => {
      try {
        await post('/account/login/magic-link/verify', { token: sp.get('token') ?? '' });
        const tenant = sp.get('tenant');
        if (tenant) {
          const w = (await workspaces()).find((x) => x.slug === tenant && x.status === 'active');
          if (w) { const to = await enterWorkspace(w.tenantId); if (to === null) return; qc.clear(); await refreshMe(); nav(to, { replace: true }); return; }
        }
        nav('/workspaces', { replace: true });
      } catch (e) { setError(e); }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <AuthShell title="로그인 링크 확인" noTenant footer={error ? <button type="button" onClick={() => nav('/login', { replace: true })}>로그인 화면으로</button> : undefined}>
      {error ? <ErrorText error={error} /> : <div className="text-sm text-slate-500">확인 중…</div>}
    </AuthShell>
  );
}
